import React from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps } from 'react-native'
import { AccessType, useCompany } from 'zens-store'
import { Button, Text } from 'zens-storybook'
import { InfoBlock, InfoTable, TimeZoneConflict } from '@dms/component'
import { usePermissions } from '@dms/hook'

const styles = StyleSheet.create({
  container: {},
  info: {
    maxWidth: '100%',
  },
  skeletonRow: {
    height: 32,
  },
  skeletonTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
})

interface Props extends ViewProps {
  companyId: number
}
const CompanyInfo = (props: Props) => {
  const { companyId, style, ...otherProps } = props
  const { t } = useTranslation()
  const company = useCompany({ companyId })
  const { name, workHours } = company.data || {}
  const { hasPermission } = usePermissions(companyId)

  const config = {
    fields: [
      {
        label: t('Name'),
        value: name,
      },
      {
        key: 'workHours',
        label: t('Working hours'),
        value: `${workHours?.startAt} - ${workHours?.endAt}`,
      },
    ],
  }

  return (
    <InfoTable
      title={t('Company details')}
      companyId={companyId}
      contentContainerStyle={styles.info}
      config={config}
      buttonTitle={t('Edit')}
      buttonHref={hasPermission(AccessType.Update, 'company') ? `/companies/${companyId}/edit` : undefined}
      style={[styles.container, style]}
      {...otherProps}
    >
      <TimeZoneConflict companyId={companyId} />
    </InfoTable>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()
  return (
    <InfoBlock.Skeleton
      title={
        <View style={styles.skeletonTitle}>
          <Text level={4}>{t('Company details')}</Text>
          <Button.Skeleton />
        </View>
      }
      {...props}
    >
      <Text.Skeleton style={styles.skeletonRow} />
      <Text.Skeleton style={styles.skeletonRow} />
    </InfoBlock.Skeleton>
  )
}
CompanyInfo.Skeleton = Skeleton

export default observer(CompanyInfo)
